import React, { useMemo } from 'react';
import type { ColDef, EditableCallbackParams, ValueGetterParams, ValueSetterParams } from 'ag-grid-community';
import { LopCrudPage } from '../../components/LopCrudPage';
import { getCategoryMap, saveCategoryMapBatch } from '../../api/categoryMap';
import type { CategoryMap } from '../../types/CategoryMap';
import { useAssetTypes } from '../../hooks/useAssetTypes';
import { useBalanceTypes } from '../../hooks/useBalanceTypes';
import { useCategories } from '../../hooks/useCategories';
import { t } from '../../i18n/t';

const editableOnAdd = (params: EditableCallbackParams<CategoryMap>) =>
  (params.data as { __status?: string } | undefined)?.__status === 'added';

export function CategoryMapPage() {
  const assetTypes = useAssetTypes();
  const balanceTypes = useBalanceTypes();
  const categories = useCategories();

  const columnDefs = useMemo<ColDef<CategoryMap>[]>(() => {
    const astLabelById = new Map<number, string>();
    const astIdByLabel = new Map<string, number>();
    (assetTypes.data ?? []).forEach((a) => {
      const label = `${a.assetTypeId} - ${a.assetTypeName}`;
      astLabelById.set(a.assetTypeId, label); astIdByLabel.set(label, a.assetTypeId);
    });
    const astLabels = Array.from(astIdByLabel.keys());

    const balLabelById = new Map<number, string>();
    const balIdByLabel = new Map<string, number>();
    (balanceTypes.data ?? []).forEach((b) => {
      const label = `${b.balanceTypeId} - ${b.balanceTypeName}`;
      balLabelById.set(b.balanceTypeId, label); balIdByLabel.set(label, b.balanceTypeId);
    });
    const balLabels = Array.from(balIdByLabel.keys());

    const catLabelById = new Map<number, string>();
    const catIdByLabel = new Map<string, number>();
    (categories.data ?? []).forEach((c) => {
      const label = `${c.categoryId} - ${c.categoryName}`;
      catLabelById.set(c.categoryId, label); catIdByLabel.set(label, c.categoryId);
    });
    const catLabels = Array.from(catIdByLabel.keys());

    return [
      {
        field: 'assetTypeId',
        headerName: t('categoryMap.columns.assetTypeId'),
        width: 260, cellDataType: 'text', editable: editableOnAdd, sort: 'asc',
        valueGetter: (p: ValueGetterParams<CategoryMap>) => { const id = p.data?.assetTypeId; if (id == null) return ''; return astLabelById.get(id) ?? String(id); },
        valueSetter: (p: ValueSetterParams<CategoryMap>) => { const id = astIdByLabel.get(p.newValue as string); if (id == null) return false; (p.data as CategoryMap).assetTypeId = id; return true; },
        cellEditor: 'agSelectCellEditor', cellEditorParams: { values: astLabels },
      },
      {
        field: 'balanceTypeId',
        headerName: t('categoryMap.columns.balanceTypeId'),
        width: 240, cellDataType: 'text', editable: editableOnAdd,
        valueGetter: (p: ValueGetterParams<CategoryMap>) => { const id = p.data?.balanceTypeId; if (id == null) return ''; return balLabelById.get(id) ?? String(id); },
        valueSetter: (p: ValueSetterParams<CategoryMap>) => { const id = balIdByLabel.get(p.newValue as string); if (id == null) return false; (p.data as CategoryMap).balanceTypeId = id; return true; },
        cellEditor: 'agSelectCellEditor', cellEditorParams: { values: balLabels },
      },
      {
        field: 'categoryId',
        headerName: t('categoryMap.columns.categoryId'),
        flex: 1, cellDataType: 'text', editable: true,
        valueGetter: (p: ValueGetterParams<CategoryMap>) => { const id = p.data?.categoryId; if (id == null) return ''; return catLabelById.get(id) ?? String(id); },
        valueSetter: (p: ValueSetterParams<CategoryMap>) => { const id = catIdByLabel.get(p.newValue as string); if (id == null) return false; (p.data as CategoryMap).categoryId = id; return true; },
        cellEditor: 'agSelectCellEditor', cellEditorParams: { values: catLabels },
      },
    ];
  }, [assetTypes.data, balanceTypes.data, categories.data]);

  const newRowDefaults = (): Partial<CategoryMap> => ({ assetTypeId: 0, balanceTypeId: 0, categoryId: 0 });

  return (
    <LopCrudPage<CategoryMap>
      resourceCode="categoryMap"
      titleKey="categoryMap.pageTitle"
      idField="idKey"
      getRows={getCategoryMap}
      saveBatch={(added, modified, deletedIds) => saveCategoryMapBatch(added, modified, deletedIds as string[])}
      columnDefs={columnDefs}
      newRowDefaults={newRowDefaults}
    />
  );
}
